// #13 Button and #14 IconButton. Ground rule 7: `can={false}` renders nothing; `blocked` gives the dashed .mc-dis,
// aria-disabled (never the disabled attribute, so the control keeps its tab stop and its tooltip), aria-describedby
// pointing at the reason rendered inline as a .mc-sr sibling (present at rest and under SSR), and activation does
// nothing. `busy` keeps the label, sets aria-busy and ignores activation. `mode="view"` renders no control.

import { forwardRef, type ButtonHTMLAttributes, type MouseEvent, type ReactNode } from "react";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { Tooltip } from "../lib/Tooltip";
import { useBusy } from "../lib/useBusy";
import { useSvgId } from "../lib/useSvgId";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { Icon, type IconName } from "./icons";

/** A rule-blocked control: his rule sentence, shown inline to assistive technology and mirrored by the tooltip. */
export interface Blocked {
  reason: string;
  /** An element already on the page that says the reason (a gate's first open row): the control is described by it
   * and renders no reason of its own; the tooltip still mirrors `reason`. */
  describedBy?: string | undefined;
}

/** The props every control shares (DC-24). */
export interface ControlProps {
  /** false: the seat may not do this here; nothing renders. */
  can?: boolean | undefined;
  blocked?: Blocked | undefined;
  /** "view": values only, no control. */
  mode?: "edit" | "view" | undefined;
  /** Capture hotspot number (plan §1.7), on the element the pointer targets. */
  hs?: Hs | undefined;
}

type NativeButton = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "style" | "className" | "children" | "disabled" | "type">;

export interface ButtonProps extends ControlProps, NativeButton {
  /** The label: one of his verbs (G §17), from a labels module. */
  children?: ReactNode;
  /** Default is the secondary button; one red primary per surface (DC-23). */
  tone?: "primary" | "blue" | "ghost" | undefined;
  sm?: boolean | undefined;
  lg?: boolean | undefined;
  wide?: boolean | undefined;
  /** Leading icon; with no children the button is square (an icon-only Button still needs aria-label). */
  icon?: IconName | undefined;
  /** Trailing icon (a chevron, an arrow). */
  after?: IconName | undefined;
  busy?: boolean | undefined;
  type?: "button" | "submit" | undefined;
  className?: string | undefined;
}

/** The reason node and the describedby wiring of a blocked control. */
export function useBlocked(blocked: Blocked | undefined, describedBy?: string | undefined) {
  const id = `${useSvgId()}-why`;
  if (!blocked) return { describedBy, reason: null, props: {} as Record<string, never> };
  if (blocked.describedBy !== undefined) {
    return {
      describedBy: [describedBy, blocked.describedBy].filter(Boolean).join(" "),
      reason: null,
      props: { "aria-disabled": true } as const,
    };
  }
  return {
    describedBy: [describedBy, id].filter(Boolean).join(" "),
    reason: <VisuallyHidden id={id}>{blocked.reason}</VisuallyHidden>,
    props: { "aria-disabled": true } as const,
  };
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { can, blocked, mode, hs, children, tone, sm, lg, wide, icon, after, busy, type = "button", className, onClick, ...rest },
  ref,
) {
  const hot = useHs(hs);
  const state = useBusy(busy);
  const block = useBlocked(blocked, rest["aria-describedby"]);
  if (can === false || mode === "view") return null;
  const iconOnly = icon !== undefined && (children === undefined || children === null || children === false);
  const handle = (event: MouseEvent<HTMLButtonElement>) => {
    if (blocked || state.busy) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };
  const button = (
    <button
      {...rest}
      ref={ref}
      type={type}
      className={cx(
        "mc-btn", tone && `mc-btn-${tone}`, sm && "mc-btn-sm", lg && "mc-btn-lg", wide && "mc-btn-wide",
        iconOnly && "mc-btn-icon", blocked && "mc-dis", className,
      )}
      aria-describedby={block.describedBy}
      {...block.props}
      {...state.props}
      {...hot}
      onClick={handle}
    >
      {icon ? <Icon name={icon} size={sm ? 16 : 18} /> : null}
      {children}
      {after ? <Icon name={after} size={16} /> : null}
    </button>
  );
  if (!blocked) return button;
  return (
    <>
      <Tooltip label={blocked.reason} mode="mirror">{button}</Tooltip>
      {block.reason}
    </>
  );
});

export interface IconButtonProps extends ControlProps, Omit<NativeButton, "aria-label"> {
  icon: IconName;
  /** The name: aria-label, and the tooltip's text (DS #14: name = aria-label = tooltip). */
  label: string;
  sm?: boolean | undefined;
  busy?: boolean | undefined;
  className?: string | undefined;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { can, blocked, mode, hs, icon, label, sm, busy, className, onClick, ...rest },
  ref,
) {
  const hot = useHs(hs);
  const state = useBusy(busy);
  const block = useBlocked(blocked, rest["aria-describedby"]);
  if (can === false || mode === "view") return null;
  const handle = (event: MouseEvent<HTMLButtonElement>) => {
    if (blocked || state.busy) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };
  return (
    <>
      <Tooltip label={blocked ? blocked.reason : label} mode="mirror">
        <button
          {...rest}
          ref={ref}
          type="button"
          className={cx("mc-ibtn", sm && "mc-ibtn-sm", blocked && "mc-dis", className)}
          aria-label={label}
          aria-describedby={block.describedBy}
          {...block.props}
          {...state.props}
          {...hot}
          onClick={handle}
        >
          <Icon name={icon} size={sm ? 16 : 18} />
        </button>
      </Tooltip>
      {block.reason}
    </>
  );
});
